import React, { useCallback, useEffect, useRef, useState } from "react";
import { createGame, tick, getGhostY } from "./engine.js";
import type { BlockByBlockGame } from "./types.js";
import { COLS, ROWS, CELL, COLORS } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

export default function BlockByBlockPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [game, setGame] = useState<BlockByBlockGame>(() => createGame());
  const [paused, setPaused] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nextRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef(game);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(0);
  const actionsRef = useRef({ left: false, right: false, down: false, rotate: false, drop: false });
  const rotateConsumed = useRef(false);
  const dropConsumed = useRef(false);
  const moveTimer = useRef(0);
  const moveDelay = useRef(0);
  const DAS_DELAY = 170;
  const DAS_RATE = 50;

  gameRef.current = game;

  const W = COLS * CELL;
  const H = ROWS * CELL;

  function resetGame() {
    setGame(createGame());
    setPaused(false);
  }

  function drawBoard(ctx: CanvasRenderingContext2D, g: BlockByBlockGame) {
    ctx.clearRect(0, 0, W, H);

    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const cell = g.board[r][c];
        const x = c * CELL;
        const y = r * CELL;
        if (cell) {
          ctx.fillStyle = COLORS[cell];
          ctx.fillRect(x, y, CELL - 1, CELL - 1);
          ctx.fillStyle = "rgba(255,255,255,0.15)";
          ctx.fillRect(x + 2, y + 2, CELL - 5, 3);
        } else {
          ctx.strokeStyle = "rgba(255,255,255,0.04)";
          ctx.lineWidth = 1;
          ctx.strokeRect(x + 0.5, y + 0.5, CELL - 1, CELL - 1);
        }
      }
    }

    if (g.current && !g.gameOver) {
      const ghostY = getGhostY(g.board, g.current);
      for (let r = 0; r < g.current.shape.length; r++) {
        for (let c = 0; c < g.current.shape[r].length; c++) {
          if (!g.current.shape[r][c]) continue;
          const gx = (g.current.x + c) * CELL;
          const gy = (ghostY + r) * CELL;
          ctx.strokeStyle = "rgba(255,255,255,0.25)";
          ctx.lineWidth = 1;
          ctx.strokeRect(gx + 1, gy + 1, CELL - 2, CELL - 2);
        }
      }

      for (let r = 0; r < g.current.shape.length; r++) {
        for (let c = 0; c < g.current.shape[r].length; c++) {
          if (!g.current.shape[r][c]) continue;
          const px = (g.current.x + c) * CELL;
          const py = (g.current.y + r) * CELL;
          ctx.fillStyle = COLORS[g.current.color];
          ctx.fillRect(px, py, CELL - 1, CELL - 1);
          ctx.fillStyle = "rgba(255,255,255,0.25)";
          ctx.fillRect(px + 2, py + 2, CELL - 5, 3);
        }
      }
    }
  }

  function drawNext(ctx: CanvasRenderingContext2D, g: BlockByBlockGame) {
    const size = 120;
    ctx.clearRect(0, 0, size, size);
    if (!g.next) return;
    const s = g.next.shape;
    const cellSize = 22;
    const ox = (size - s[0].length * cellSize) / 2;
    const oy = (size - s.length * cellSize) / 2;
    for (let r = 0; r < s.length; r++) {
      for (let c = 0; c < s[r].length; c++) {
        if (!s[r][c]) continue;
        ctx.fillStyle = COLORS[g.next.color];
        ctx.fillRect(ox + c * cellSize, oy + r * cellSize, cellSize - 1, cellSize - 1);
      }
    }
  }

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;
    drawBoard(ctx, game);
  }, [game]);

  useEffect(() => {
    if (!nextRef.current) return;
    const ctx = nextRef.current.getContext("2d");
    if (!ctx) return;
    drawNext(ctx, game);
  }, [game.next]);

  useEffect(() => {
    lastTimeRef.current = performance.now();
    function loop(time: number) {
      const dt = time - lastTimeRef.current;
      lastTimeRef.current = time;
      const g = gameRef.current;
      if (!g.gameOver && !paused && dt < 500) {
        const raw = actionsRef.current;
        const actions = { left: false, right: false, down: raw.down, rotate: false, drop: false };

        if (raw.rotate) {
          if (rotateConsumed.current) actions.rotate = false;
          else { actions.rotate = true; rotateConsumed.current = true; }
        } else {
          rotateConsumed.current = false;
        }

        if (raw.drop) {
          if (dropConsumed.current) actions.drop = false;
          else { actions.drop = true; dropConsumed.current = true; }
        } else {
          dropConsumed.current = false;
        }

        if (raw.left || raw.right) {
          moveTimer.current += dt;
          const effectiveDelay = moveDelay.current === 0 ? 0 : moveDelay.current;
          if (moveTimer.current >= effectiveDelay) {
            if (raw.left) actions.left = true;
            if (raw.right) actions.right = true;
            moveTimer.current = 0;
            if (moveDelay.current === 0) moveDelay.current = DAS_DELAY;
            else moveDelay.current = DAS_RATE;
          }
        } else {
          moveTimer.current = 0;
          moveDelay.current = 0;
        }

        setGame((prev) => tick(prev, dt, actions));
      }
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.repeat && (e.key === "ArrowUp" || e.key === " " || e.key === "Space")) return;
      switch (e.key) {
        case "ArrowLeft": case "a": case "A": e.preventDefault(); actionsRef.current.left = true; break;
        case "ArrowRight": case "d": case "D": e.preventDefault(); actionsRef.current.right = true; break;
        case "ArrowDown": case "s": case "S": e.preventDefault(); actionsRef.current.down = true; break;
        case "ArrowUp": case "w": case "W": e.preventDefault(); actionsRef.current.rotate = true; break;
        case " ": case "Space": e.preventDefault(); actionsRef.current.drop = true; break;
        case "p": case "P": e.preventDefault(); setPaused((p) => !p); break;
      }
    }
    function handleKeyUp(e: KeyboardEvent) {
      switch (e.key) {
        case "ArrowLeft": case "a": case "A": actionsRef.current.left = false; break;
        case "ArrowRight": case "d": case "D": actionsRef.current.right = false; break;
        case "ArrowDown": case "s": case "S": actionsRef.current.down = false; break;
        case "ArrowUp": case "w": case "W": actionsRef.current.rotate = false; rotateConsumed.current = false; break;
        case " ": case "Space": actionsRef.current.drop = false; break;
      }
    }
    window.addEventListener("keydown", handleKey);
    window.addEventListener("keyup", handleKeyUp);
    return () => { window.removeEventListener("keydown", handleKey); window.removeEventListener("keyup", handleKeyUp); };
  }, []);

  return (
    <div className="game-shell">
      <ConfettiSplash active={game.gameOver} winnerName="Game Over" />
      {!hideTitle && <h1 className="title">Block By Block</h1>}

      <section className="control-panel">
        <p className="status">{game.gameOver ? "Game Over!" : paused ? "Paused" : `Level ${game.level}  ·  Lines ${game.lines}  ·  Score ${game.score}`}</p>
        <div className="actions">
          <button onClick={resetGame}>New Game</button>
          <button onClick={() => setPaused((p) => !p)}>{paused ? "Resume" : "Pause (P)"}</button>
        </div>
      </section>

      <section className="board-panel" style={{ display: "flex", gap: 16, alignItems: "flex-start", justifyContent: "center" }}>
        <canvas ref={canvasRef} width={W} height={H} className="bbb-canvas" />
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 130 }}>
          <div style={{ background: "#061424", borderRadius: 10, padding: 10, border: "1px solid rgba(0,180,255,0.18)" }}>
            <p style={{ margin: "0 0 8px", fontWeight: 700, color: "#8ab4d0", fontSize: "0.85rem" }}>Next</p>
            <canvas ref={nextRef} width={120} height={120} style={{ display: "block", margin: "0 auto" }} />
          </div>
          <div className="how-to-play" style={{ margin: 0 }}>
            <p className="how-title">Controls</p>
            <p>← → / A D : Move</p>
            <p>↑ / W : Rotate</p>
            <p>↓ / S : Soft drop</p>
            <p>Space : Hard drop</p>
            <p>P : Pause</p>
          </div>
        </div>
      </section>
    </div>
  );
}
